import { PrintTagTemplateDetailFieldsFragment } from '@/types';

export interface ZPLData {
  displayName?: string;
  externalItemId?: string;
  formattedPrice?: string;
  packDisplayString?: string;
}

/**
 * ZPL characters that would otherwise be read as syntax rather than content,
 * mapped to the `_XX` hex escapes that print them literally. `^` and `~` are
 * ZPL's command prefixes, and a field put into hex mode by `^FH` treats `_`
 * as its escape character.
 */
const ZPL_HEX_ESCAPES: Record<string, string> = {
  '^': '_5E',
  _: '_5F',
  '~': '_7E',
};

/**
 * Replaces every escapable character in one pass, so an escape this function
 * introduces is never re-escaped by a later replacement.
 */
const escapeZPLValue = (value: string) =>
  value.replace(/[_^~]/g, (character) => ZPL_HEX_ESCAPES[character]);

/**
 * Renders a ZPL template string by finding all {variableName} placeholders
 * and replacing them with values from the provided data object.
 * Missing or `null`/`undefined` optional fields in the data object will be
 * replaced with an empty string in the template.
 *
 * Values are escaped so that item data prints as typed: without it, an item
 * code of `SKU_12` prints as `SKU` followed by byte 0x12, and a `^` ends the
 * field so the rest of the value is parsed as commands.
 *
 * @param templateString The ZPL template string from the backend.
 * @param data An object containing the values for the placeholders.
 * @returns The rendered ZPL string.
 */
export const renderZPLTemplate = (
  templateString: string,
  data: ZPLData,
): string => {
  // Use a single regex to find all placeholders of the form {key}
  // \{   -> matches a literal opening curly brace
  // ([a-zA-Z0-9_]+) -> captures the variable name (letters, numbers, underscore)
  // \}   -> matches a literal closing curly brace
  const placeholderRegex = /\{([a-zA-Z0-9_]+)\}/g;

  // Hex escapes only mean anything to a field the template put into hex mode.
  // In a template without `^FH`, `_5F` would itself print literally, so those
  // values are passed through untouched.
  const supportsHexEscapes = templateString.includes('^FH');

  return templateString.replace(placeholderRegex, (_match, key) => {
    const value = data[key as keyof ZPLData];

    if (value === undefined || value === null) {
      return '';
    }

    return supportsHexEscapes ? escapeZPLValue(value) : value;
  });
};

export const getTagPrintingTemplatesForOffline = (
  templates: PrintTagTemplateDetailFieldsFragment[],
) =>
  templates.filter(
    ({ offline_printer_text_template }) =>
      offline_printer_text_template !== null,
  );

/**
 * Fields a user can fill in by hand when printing a custom tag, ordered as
 * they are presented on the custom tag form. Each field maps to a
 * {placeholder} in a template's ZPL text template.
 */
export const CUSTOM_TAG_FIELDS: { key: keyof ZPLData; labelKey: string }[] = [
  { key: 'displayName', labelKey: 'item_name' },
  { key: 'externalItemId', labelKey: 'item_code' },
  { key: 'packDisplayString', labelKey: 'pack_size' },
  { key: 'formattedPrice', labelKey: 'price' },
];

export const CUSTOM_TAG_FIELD_MAX_LENGTH = 60;

/**
 * Returns the subset of `CUSTOM_TAG_FIELDS` the given ZPL template actually
 * references, so the custom tag form only asks for values that get printed.
 */
export const getCustomTagFieldsForTemplate = (templateString: string) =>
  CUSTOM_TAG_FIELDS.filter(({ key }) => templateString.includes(`{${key}}`));
